// frontend/src/pages/SellerOrdersPage.jsx
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Eye, Loader2, Search, RefreshCw, Copy, 
  CheckCircle, Package, Truck, CheckSquare, XCircle,
  Clock, CreditCard, ShoppingBag, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  Plus
} from 'lucide-react';
import { PageSEO } from '../components/SEO/PageSEO';
import config from '../config';
import CreateOrderModal from '../components/Orders/CreateOrderModal';
import { toast } from 'react-hot-toast'; // ✅ اضافه کردن این خط

export const SellerOrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [viewMode, setViewMode] = useState('table');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const pageSize = 20;

  useEffect(() => {
    const handleResize = () => {
      setViewMode(window.innerWidth < 768 ? 'card' : 'table');
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('page_size', pageSize);
      if (statusFilter) params.append('status', statusFilter);
      
      const url = config.getApiUrl(`/orders/seller-orders/?${params.toString()}`);
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
      
      if (!response.ok) throw new Error('خطا در دریافت سفارشات');
      
      const data = await response.json();
      console.log('Seller orders data:', data);
      
      setOrders(data.results || []);
      setTotalCount(data.count || 0);
      setTotalPages(Math.ceil((data.count || 0) / pageSize));
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('خطا در دریافت سفارشات'); // ✅ اضافه کردن toast
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // ✅ تابع برای اضافه کردن سفارش جدید به لیست
  const handleOrderCreated = (newOrderData) => {
    if (newOrderData?.order) {
      setOrders([newOrderData.order, ...orders]);
      setTotalCount(prev => prev + 1);
      toast.success('سفارش جدید با موفقیت ثبت شد'); // ✅ این خط قبلاً مشکل داشت
    }
  };

  const getOrderNumber = (order) => {
    return order.order_number || order.id.toString().slice(0, 8);
  };

  const getStatusBadge = (status, isCard = false) => {
    const statusMap = {
      'pending': { text: 'در انتظار پرداخت', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      'paid': { text: 'پرداخت شده', color: 'bg-blue-100 text-blue-800', icon: CreditCard },
      'processing': { text: 'در حال پردازش', color: 'bg-purple-100 text-purple-800', icon: Package },
      'shipped': { text: 'ارسال شده', color: 'bg-orange-100 text-orange-800', icon: Truck },
      'delivered': { text: 'تحویل داده شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'completed': { text: 'تکمیل شده', color: 'bg-green-100 text-green-800', icon: CheckCircle },
      'cancelled': { text: 'لغو شده', color: 'bg-red-100 text-red-800', icon: XCircle },
    };
    const info = statusMap[status] || statusMap.pending;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${info.color}`}>
        {!isCard && <Icon className="w-3 h-3" />}
        {info.text}
      </span>
    );
  };

  const getCustomerName = (order) => {
    if (order.user_detail?.username) return order.user_detail?.username;
    if (order.user_detail?.phone) return order.user_detail?.phone;
    if (order.user?.username) return order.user?.username;
    if (order.user?.phone) return order.user?.phone;
    if (order.customer?.username) return order.customer?.username;
    if (order.customer?.phone) return order.customer?.phone;
    return 'مشتری';
  };

  const getCustomerPhone = (order) => {
    if (order.user_detail?.phone) return order.user_detail?.phone;
    if (order.user?.phone) return order.user?.phone;
    if (order.customer?.phone) return order.customer?.phone;
    return '-';
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    if (!window.confirm(`آیا از تغییر وضعیت سفارش به "${getStatusText(newStatus)}" اطمینان دارید؟`)) {
      return;
    }
    
    setActionLoading(orderId);
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl(`/orders/${orderId}/update-tracking/`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success(`وضعیت سفارش به "${getStatusText(newStatus)}" تغییر یافت`);
        fetchOrders();
      } else {
        const error = await response.json();
        toast.error('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('خطا در ارتباط با سرور');
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'pending': 'در انتظار پرداخت',
      'paid': 'پرداخت شده',
      'processing': 'در حال پردازش',
      'shipped': 'ارسال شده',
      'delivered': 'تحویل داده شده',
      'completed': 'تکمیل شده',
      'cancelled': 'لغو شده',
    };
    return statusMap[status] || status;
  };

  const canUpdateStatus = (currentStatus) => {
    return ['paid', 'processing', 'shipped'].includes(currentStatus);
  };

  const getNextStatusOptions = (currentStatus) => {
    const options = {
      'paid': { status: 'processing', label: 'تایید و شروع پردازش', icon: Package, color: 'bg-blue-600' },
      'processing': { status: 'shipped', label: 'ارسال سفارش', icon: Truck, color: 'bg-orange-600' },
      'shipped': { status: 'delivered', label: 'تایید تحویل', icon: CheckSquare, color: 'bg-green-600' },
    };
    return options[currentStatus];
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const copyOrderId = (id) => {
    navigator.clipboard.writeText(id);
    toast.success('شماره سفارش کپی شد');
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '۰';
    return parseInt(price).toLocaleString('fa-IR');
  };

  const filteredOrders = orders.filter(order => 
    getCustomerName(order).includes(searchTerm) ||
    getOrderNumber(order).includes(searchTerm) ||
    getCustomerPhone(order).includes(searchTerm)
  );

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  if (loading && orders.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="سفارشات | پنل فروشنده" noindex={true} />
      
      <div className="min-h-screen bg-gray-50 py-4 md:py-8">
        <div className="container mx-auto px-3 md:px-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4 md:mb-6">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">سفارشات</h1>
              <p className="text-xs md:text-sm text-gray-500 mt-1">مدیریت و پیگیری سفارشات مشتریان</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition text-sm"
              >
                <Plus className="w-4 h-4" />
                ثبت سفارش جدید
              </button>
              <button
                onClick={() => { setRefreshing(true); fetchOrders(); }}
                disabled={refreshing}
                className="flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 border rounded-lg hover:bg-gray-50 transition text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                بروزرسانی
              </button>
            </div>
          </div>

          {/* فیلترها */}
          <div className="bg-white rounded-xl shadow-sm p-3 md:p-4 mb-4 md:mb-6">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="جستجو در سفارشات..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pr-10 pl-3 py-1.5 md:py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                />
              </div>
              
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 md:px-4 md:py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">همه وضعیت‌ها</option>
                <option value="pending">در انتظار پرداخت</option>
                <option value="paid">پرداخت شده</option>
                <option value="processing">در حال پردازش</option>
                <option value="shipped">ارسال شده</option>
                <option value="delivered">تحویل داده شده</option>
                <option value="cancelled">لغو شده</option>
              </select>
            </div>
          </div>

          {/* نمایش تعداد نتایج */}
          <div className="text-xs md:text-sm text-gray-500 mb-3 md:mb-4 text-right">
            نمایش {startItem} تا {endItem} از {totalCount} سفارش
          </div>

          {/* حالت جدول (دسکتاپ) */}
          {viewMode === 'table' && (
            <div className="bg-white rounded-xl shadow-sm overflow-hidden hidden md:block">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">شماره سفارش</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">مشتری</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">شماره تماس</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">مبلغ کل</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">تعداد اقلام</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">تاریخ ثبت</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredOrders.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                          سفارشی یافت نشد
                        </td>
                      </tr>
                    ) : (
                      filteredOrders.map((order) => {
                        const nextAction = getNextStatusOptions(order.status);
                        const canUpdate = canUpdateStatus(order.status);
                        const isPendingAction = actionLoading === order.id;
                        
                        return (
                          <tr key={order.id} className="hover:bg-gray-50 transition">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="font-mono text-sm font-medium text-gray-900">
                                  #{getOrderNumber(order)}
                                </span>
                                <button
                                  onClick={() => copyOrderId(order.id)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="کپی شماره سفارش"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {getCustomerName(order)}
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {getCustomerPhone(order)}
                            </td>
                            <td className="px-4 py-3 font-medium text-gray-900">
                              {formatPrice(order.final_amount || order.total_amount)} تومان
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {order.items?.length || 0} قلم
                            </td>
                            <td className="px-4 py-3">
                              {getStatusBadge(order.status, false)}
                            </td>
                            <td className="px-4 py-3 text-gray-500 text-sm">
                              {new Date(order.created_at).toLocaleDateString('fa-IR')}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex gap-2">
                                <Link
                                  to={`/orders/${order.id}`}
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                  title="مشاهده جزئیات"
                                >
                                  <Eye className="w-4 h-4" />
                                </Link>
                                
                                {canUpdate && nextAction && (
                                  <button
                                    onClick={() => updateOrderStatus(order.id, nextAction.status)}
                                    disabled={isPendingAction}
                                    className={`inline-flex items-center gap-1 px-2 py-1 text-white rounded-lg transition ${nextAction.color} hover:opacity-80 disabled:opacity-50 text-xs`}
                                    title={nextAction.label}
                                  >
                                    {isPendingAction ? (
                                      <Loader2 className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <nextAction.icon className="w-3 h-3" />
                                    )}
                                    <span className="hidden sm:inline">{nextAction.label}</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* حالت کارت (موبایل) */}
          {viewMode === 'card' && (
            <div className="space-y-3">
              {filteredOrders.length === 0 ? (
                <div className="bg-white rounded-xl p-8 text-center text-gray-500">
                  سفارشی یافت نشد
                </div>
              ) : (
                filteredOrders.map((order) => {
                  const nextAction = getNextStatusOptions(order.status);
                  const canUpdate = canUpdateStatus(order.status);
                  const isPendingAction = actionLoading === order.id;
                  
                  return (
                    <div key={order.id} className="bg-white rounded-xl shadow-sm p-4 hover:shadow-md transition">
                      <div className="flex justify-between items-start mb-3 pb-2 border-b border-gray-100">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-medium text-gray-900">
                            #{getOrderNumber(order)}
                          </span>
                          <button
                            onClick={() => copyOrderId(order.id)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        {getStatusBadge(order.status, true)}
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-500">مشتری:</span>
                          <span className="font-medium text-gray-900">{getCustomerName(order)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">شماره تماس:</span>
                          <span className="text-gray-600">{getCustomerPhone(order)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">مبلغ کل:</span>
                          <span className="font-bold text-blue-600">{formatPrice(order.final_amount || order.total_amount)} تومان</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">تعداد اقلام:</span>
                          <span className="text-gray-600">{order.items?.length || 0} قلم</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">تاریخ ثبت:</span>
                          <span className="text-gray-500 text-xs">
                            {new Date(order.created_at).toLocaleDateString('fa-IR')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="mt-3 pt-2 border-t border-gray-100 flex gap-2">
                        <Link
                          to={`/orders/${order.id}`}
                          className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-600 py-2 rounded-lg hover:bg-blue-100 transition text-sm"
                        >
                          <Eye className="w-4 h-4" />
                          جزئیات
                        </Link>
                        
                        {canUpdate && nextAction && (
                          <button
                            onClick={() => updateOrderStatus(order.id, nextAction.status)}
                            disabled={isPendingAction}
                            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-lg transition text-white text-sm ${nextAction.color} hover:opacity-80 disabled:opacity-50`}
                          >
                            {isPendingAction ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <nextAction.icon className="w-4 h-4" />
                            )}
                            {nextAction.label}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* صفحه‌بندی */}
          {totalPages > 1 && (
            <div className="mt-6 md:mt-8 flex justify-center items-center gap-1 md:gap-2 flex-wrap">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="اولین صفحه"
              >
                <ChevronsRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="قبلی"
              >
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              
              <div className="flex gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 md:w-10 md:h-10 rounded-lg transition text-sm ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'border hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="بعدی"
              >
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              </button>
              <button
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage === totalPages}
                className="p-1.5 md:p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                title="آخرین صفحه"
              >
                <ChevronsLeft className="w-4 h-4 md:w-5 md:h-5" />
              </button>
            </div>
          )}
          
          <div className="mt-4 text-center text-xs md:text-sm text-gray-500">
            نمایش {filteredOrders.length} از {totalCount} سفارش
          </div>
        </div>
      </div>

      {/* ✅ مودال ثبت سفارش */}
      <CreateOrderModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleOrderCreated}
      />
    </>
  );
};